/* ==========================================================================
   we-pdp-reviews.js - Customer Reviews behaviour around Judge.me's own
   filter/sort/pagination lifecycle.

   Loaded by sections/we-pdp-reviews.liquid. Vanilla, no dependencies.

   It does six things, and nothing else:
     1. makes "See all reviews" a full reset (rating filter AND sort)
     2. holds the section's footprint while Judge.me is fetching
     3. shows an empty state when a completed request returned no reviews
     4. re-anchors the viewport on the controls row once the result has rendered
     5. strips a leading ">> " Judge.me itself renders before a merchant reply
     6. makes the media viewer's close control keyboard/screen-reader
        reachable and lets Escape close it (Judge.me's popup has neither)

   It used to also remove Judge.me's own "Only Pictures" sort option
   (value with-pictures, label from jdgmSettings.widget_sorting_with_
   pictures_text) - restored, not rebuilt: this product now has at least
   one picture review, so the option is a real, working filter again
   rather than the guaranteed-empty dead end it was when that removal was
   written. Confirmed live, end to end, before removing the strip code:
   selecting it fires Judge.me's own request with sort_by=with_pictures and
   returns only reviews that actually contain a picture - no custom
   filtering logic anywhere in this file, before or after.

   It renders no reviews, fetches nothing, and displays no review count. All
   review data and every request stay Judge.me's.

   ------------------------------------------------------------------------
   THE LIFECYCLE THIS HANGS OFF - measured, not assumed
   ------------------------------------------------------------------------
   Judge.me bundles its own jQuery 3.6.0 and exposes it as jdgm.jQuery, with
   ajaxSettings.global true. Subscribing there gives the four states this needs
   as real, distinct signals - verified live on this widget: ajaxSend,
   ajaxError and ajaxComplete all fire on jdgm.jQuery(document).

     ajaxSend      -> REQUEST START     (lock footprint, show "Updating…")
     ajaxSuccess   -> REQUEST OK        (remember that this cycle succeeded)
     ajaxError     -> REQUEST FAILED    (release, show nothing - a failure is
                                         NOT an empty result set)
     ajaxComplete  -> REQUEST FINISHED  (release footprint, then judge empty
                                         and re-anchor)

   That is why there are no timeouts and no polling anywhere in this file:
   every transition here is driven by Judge.me's own request. The one
   exception is the reply-arrow cleanup (see observeReplyArrows), which
   uses a narrowly-scoped MutationObserver instead, for a reason specific
   to that one piece of DOM - documented in full at that function, not
   repeated here.

   Judge.me also publishes doneCustomizeReviews / doneAppendMoreReviews, but
   only through its internal jQuery and only on some paths; the global ajax
   events above cover filtering, sorting AND pagination uniformly, so they are
   the single hook used here.
   ========================================================================== */
(function () {
  'use strict';

  var APP = '[data-wep-cr-app]';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Judge.me's review request. Both halves must match so unrelated Shopify or
     app AJAX on the page can never drive this section's state. */
  function isJudgeMeReviewRequest(settings) {
    var url = (settings && settings.url) || '';
    return url.indexOf('judge.me') !== -1 && url.indexOf('reviews_for_widget') !== -1;
  }

  function apps() {
    return Array.prototype.slice.call(document.querySelectorAll(APP));
  }

  function jdgmJQuery() {
    var jq = window.jdgm && (window.jdgm.jQuery || window.jdgm.$);
    return typeof jq === 'function' ? jq : null;
  }

  /* ------------------------------------------------------------------------
     1. "See all reviews" as a full reset
     ------------------------------------------------------------------------
     Judge.me's own _resetFilterAndSearch() clears the rating filter, the search
     box and keyword chips - but NOT the sort. Its clear-filter handler then
     issues the fetch itself, and jdgm.ajaxParamsFor() builds that request from
     widget.data('sort-by') / ('sort-dir').

     So instead of triggering anything, this listens in the CAPTURE phase and
     rewrites those two values to Judge.me's defaults BEFORE Judge.me's own
     bubble-phase handler reads them. Judge.me then performs exactly one fetch,
     already unsorted. No second request, no synthetic clicks on the sort
     control, no custom AJAX, and Judge.me's reset stays in charge of the
     filter, the histogram state and the page.

     The <select> is set too so the visible control agrees with the request.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest(APP)) return;
    if (!target.closest('.jdgm-histogram__clear-filter')) return;

    var widget = target.closest('#judgeme_product_reviews');
    var jq = jdgmJQuery();
    if (!widget || !jq) return;

    /* Judge.me's own defaults for "Most Recent". */
    jq(widget).data('sort-by', 'created_at').data('sort-dir', 'desc');

    var select = widget.querySelector('.jdgm-sort-dropdown');
    /* Value only - deliberately no change event, which would make Judge.me
       fire a second request for the same reset. */
    if (select) select.value = 'most-recent';
  }, true);

  /* ------------------------------------------------------------------------
     2-4. Footprint, empty state and re-anchoring
     ------------------------------------------------------------------------ */
  function statusEl(app, name) {
    return app.querySelector('[data-wep-cr-' + name + ']');
  }

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  function onRequestStart() {
    apps().forEach(function (app) {
      /* Hold the height the section already occupies, so the reviews vanishing
         cannot collapse it and pull the next section up into view. A floor, not
         a fixed height - released the moment the request finishes. */
      var h = Math.round(app.getBoundingClientRect().height);
      if (h > 0) app.style.minHeight = h + 'px';
      hide(statusEl(app, 'empty'));
      show(statusEl(app, 'loading'));
    });
  }

  function releaseFootprint(app) {
    app.style.minHeight = '';
    hide(statusEl(app, 'loading'));
  }

  /* Reviews that belong to the current result set. "Reviews in Other Languages"
     is a separate Judge.me block (and hidden on this PDP), so it must not make
     an empty result look populated. */
  function resultReviewCount(widget) {
    var n = 0;
    Array.prototype.forEach.call(widget.querySelectorAll('.jdgm-rev'), function (rev) {
      if (!rev.closest('.jdgm-rev-widg__other-lang')) n++;
    });
    return n;
  }

  /* ------------------------------------------------------------------------
     5. Strip Judge.me's own leading ">> " from a merchant reply header
     ------------------------------------------------------------------------
     Read straight off the live widget, not assumed: a merchant reply renders
     as <div class="jdgm-rev__replier-wrapper">&gt;&gt;
     <b class="jdgm-rev__replier">{shop name}</b> replied:</div> - three child
     nodes, with ">> " as the FIRST node's own real text, immediately before
     the shop name. Confirmed live that this is an actual DOM text node (it
     shows up in .textContent), not a CSS ::before - so it can only be
     corrected by editing that text node directly, never by hiding it with
     CSS (which would leave the characters selectable/readable by a screen
     reader) and never by editing Judge.me's own JS or settings.

     Trims only a leading run of '>' plus whitespace, so it never touches the
     shop name or "replied:", and is idempotent - safe to call again on an
     already-trimmed node, which is what makes the observer below safe to
     leave running for the life of the page rather than disconnecting it
     after one use. */
  var REPLY_ARROW_PREFIX = /^\s*>+\s*/;

  function stripReplyArrows(widget) {
    Array.prototype.forEach.call(
      widget.querySelectorAll('.jdgm-rev__replier-wrapper'),
      function (wrapper) {
        var first = wrapper.firstChild;
        if (first && first.nodeType === Node.TEXT_NODE) {
          first.textContent = first.textContent.replace(REPLY_ARROW_PREFIX, '');
        }
      }
    );
  }

  /* WHY A MutationObserver, when the rest of this file deliberately avoids
     one - measured live, not assumed, and only after a first attempt at
     this fix (hanging the cleanup off jQuery's ajaxComplete, even called
     synchronously with no requestAnimationFrame) turned out not to be
     enough: a real device still showed ">>". Instrumented a single sort
     change end to end to find out why. Judge.me fired TWO ajaxSend
     requests for that one change (not assumed - logged); both completed
     (two ajaxComplete events); and .jdgm-rev__replier-wrapper's children
     were actually torn down and rebuilt 4-8ms AFTER the SECOND
     ajaxComplete had already fired - reproduced twice, consistently, in
     this same browser. So ajaxComplete firing does not reliably mean
     Judge.me has finished writing this specific piece of DOM; anything
     hung off that event, sync or deferred by a frame, can run before the
     real write and simply miss it. That is the actual reason the previous
     fix tested correctly here but still left ">>" visible on a real
     device: the write this depends on has no fixed, reliable relationship
     to any single event this file could otherwise listen for.

     A MutationObserver sidesteps needing to know WHEN Judge.me writes the
     DOM - it reacts to the write itself, whenever it genuinely happens.
     Scoped to childList/subtree on this section's OWN app wrapper only
     (never document or body - one Reviews section, not the page), which
     already exists at page load, so nothing here needs to poll or guess
     when to start observing either. childList only, deliberately NOT
     characterData: stripReplyArrows only ever edits a text node's
     .textContent, which fires as a characterData mutation, not childList -
     so the observer can never see its own edit and re-fire on itself, no
     disconnect/reconnect guard required. One observer per app, guarded
     against double-attaching, never disconnected - same reasoning as the
     ajax subscription below never being torn down: the section exists for
     the life of the page. */
  function observeReplyArrows(app) {
    if (app.__wepReplyObserver) return;

    var widget = app.querySelector('#judgeme_product_reviews');
    if (widget) stripReplyArrows(widget);

    var observer = new MutationObserver(function () {
      var w = app.querySelector('#judgeme_product_reviews');
      if (w) stripReplyArrows(w);
    });
    observer.observe(app, { childList: true, subtree: true });
    app.__wepReplyObserver = observer;
  }

  /* The already-measured PDP sticky-header offset. we-pdp.js writes
     --wep-sticky-top onto every .we-pdp section from the real rendered header
     height, so this reuses that instead of guessing a pixel value. */
  function stickyOffset(app) {
    var section = app.closest('.we-pdp') || app;
    var raw = window.getComputedStyle(section).getPropertyValue('--wep-sticky-top');
    var n = parseFloat(raw);
    return isNaN(n) ? 96 : n;
  }

  /* Anchor on the controls row - Judge.me's own .jdgm-row-actions, which
     survives every re-render - so the heading, summary, histogram, Write a
     Review and the sort control are all in view above the new results. */
  function scrollTarget(app) {
    var widget = app.querySelector('#judgeme_product_reviews');
    return (widget && widget.querySelector('.jdgm-row-actions')) || app;
  }

  function reanchor(app) {
    var target = scrollTarget(app);
    if (!target) return;

    var desired = stickyOffset(app);
    var top = target.getBoundingClientRect().top;
    /* Leave the page alone when the controls are already comfortably placed -
       no movement for movement's sake. */
    var slack = Math.max(160, Math.round(window.innerHeight * 0.3));
    if (top >= desired - 8 && top <= desired + slack) return;

    window.scrollTo({
      top: Math.max(0, window.scrollY + top - desired),
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  }

  function onRequestFinished(succeeded) {
    apps().forEach(function (app) {
      var widget = app.querySelector('#judgeme_product_reviews');
      releaseFootprint(app);
      if (!widget) return;

      /* Reply cleanup does NOT happen here (or anywhere in this function)
         any more - see observeReplyArrows further up for why hanging it
         off ajaxComplete, sync or deferred, was tried and measured to be
         unreliable: Judge.me's actual DOM write for the reply can land a
         few milliseconds AFTER this event already fired. A
         MutationObserver on the app wrapper now reacts to that write
         directly, independent of this function entirely.

         One frame, so the measurement and the empty test see the DOM
         Judge.me has actually finished writing - not a timeout guess. */
      window.requestAnimationFrame(function () {
        /* Only a COMPLETED, SUCCESSFUL request can mean "no reviews match".
           A network failure leaves Judge.me's own behaviour untouched and says
           nothing to the customer here. */
        if (succeeded && resultReviewCount(widget) === 0) {
          show(statusEl(app, 'empty'));
        } else {
          hide(statusEl(app, 'empty'));
        }
        reanchor(app);
      });
    });
  }

  function bindLifecycle() {
    var jq = jdgmJQuery();
    if (!jq) return false;

    var succeeded = false;

    jq(document)
      .on('ajaxSend', function (event, xhr, settings) {
        if (!isJudgeMeReviewRequest(settings)) return;
        succeeded = false;
        onRequestStart();
      })
      .on('ajaxSuccess', function (event, xhr, settings) {
        if (!isJudgeMeReviewRequest(settings)) return;
        succeeded = true;
      })
      .on('ajaxComplete', function (event, xhr, settings) {
        if (!isJudgeMeReviewRequest(settings)) return;
        onRequestFinished(succeeded);
      });

    return true;
  }

  /* ------------------------------------------------------------------------
     6. Media viewer - close-button accessibility and Escape to close
     ------------------------------------------------------------------------
     Judge.me's gallery/lightbox popup (Magnific Popup) is appended to
     <body> when a customer clicks a photo/video thumbnail - a separate DOM
     tree from the review widget everything above hangs off, confirmed live.
     Two confirmed gaps in Judge.me's own markup, neither fixable in CSS:

       - Escape does not close it (tested live: pressing it while the popup
         is open leaves it open and <html> still overflow:hidden).
       - its only close control is <div class="jm-mfp-close">×</div> with
         tabindex="-1", no role and no aria-label (read live) - unreachable
         by keyboard Tab and silent to a screen reader.

     Both are fixed by working WITH Judge.me's own close control - clicking
     it, never a second close mechanism, never touching Judge.me's own
     open/close logic or its scroll-lock (verified separately to already
     correctly restore scroll position on close - see we-pdp-reviews.css
     section 11 for that verification).
     ------------------------------------------------------------------------ */
  function galleryPopup() {
    return document.querySelector('.jm-mfp-wrap.jdgm-gallery-popup');
  }

  function fixCloseButtonA11y(popup) {
    var close = popup.querySelector('.jm-mfp-close');
    if (!close || close.__wepA11y) return;
    close.__wepA11y = true;
    close.setAttribute('role', 'button');
    close.setAttribute('tabindex', '0');
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      close.click();
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('.jdgm-gallery__thumbnail-link')) return;

    /* Judge.me builds the popup in its own click handler on the same
       thumbnail; this runs after it, in the bubble phase, so the popup may
       not exist on this exact line yet - one frame is enough for it to
       land, the same reasoning onRequestFinished's rAF above uses. */
    window.requestAnimationFrame(function () {
      var popup = galleryPopup();
      if (popup) fixCloseButtonA11y(popup);
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var popup = galleryPopup();
    if (!popup) return;
    var close = popup.querySelector('.jm-mfp-close');
    /* A real click on Judge.me's own control, not a second implementation
       of "close" - whatever cleanup Judge.me itself does on close (scroll
       restore, releasing a playing video, DOM teardown) runs exactly as it
       would from a mouse click. */
    if (close) close.click();
  });

  /* ------------------------------------------------------------------------
     7. Write-a-review modal - close-button accessibility and Escape to close
     ------------------------------------------------------------------------
     Judge.me's star-rating/write-review modal (.jdgm-write-review-modal) is
     ALSO appended directly to <body> - confirmed live, a separate DOM tree
     from the review widget, exactly like the gallery/lightbox popup above,
     and equally out of we-pdp-reviews.css's .we-pdp #judgeme_product_reviews
     scope (that CSS cannot reach this modal at all; nothing here has ever
     styled it before this fix).

     Two confirmed gaps in Judge.me's own markup, the same shape as the
     gallery popup's (see section 6), found the same way - read live, not
     assumed:
       - its top-right "X" (.jdgm-write-review-modal__close-btn) is a real,
         properly aria-labelled <button>, but ships tabindex="-1" - reachable
         by mouse, invisible to keyboard Tab.
       - Escape does not close the modal (tested live: dispatching it while
         open leaves the modal mounted).
     Back/Next/the in-flow "Close" button on the final step are ordinary
     <button>s with no tabindex override, so they are already keyboard-
     reachable - only the top-right "X" needed the tabindex fix.

     This does not touch, resize or reposition Judge.me's own multi-step
     slider (.jdgm-write-review-modal__sections) - that is Judge.me's own
     layout/animation mechanism and stays completely alone here; this only
     restores keyboard reachability to a control Judge.me's own markup
     already renders, exactly as section 6 does for the gallery popup. */
  function writeReviewModal() {
    return document.querySelector('.jdgm-write-review-modal');
  }

  function fixWriteReviewCloseA11y(modal) {
    var close = modal.querySelector('.jdgm-write-review-modal__close-btn');
    if (!close || close.__wepA11y) return;
    close.__wepA11y = true;
    close.setAttribute('tabindex', '0');
  }

  var writeReviewObserver = new MutationObserver(function () {
    var modal = writeReviewModal();
    if (modal) fixWriteReviewCloseA11y(modal);
  });
  writeReviewObserver.observe(document.body, { childList: true });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var modal = writeReviewModal();
    if (!modal) return;
    var close = modal.querySelector('.jdgm-write-review-modal__close-btn');
    /* A real click on Judge.me's own control, same reasoning as section 6:
       whatever cleanup Judge.me itself does on close runs exactly as it
       would from a mouse click - no second close implementation. */
    if (close) close.click();
  });

  function init() {
    /* Attached once, immediately: the app wrapper is server-rendered
       markup (this section's own Liquid output), always present by the
       time this script runs, so - unlike bindLifecycle below - there is no
       "Judge.me might not exist yet" case to retry on window load for.
       Whatever Judge.me writes into it after this point, on any timeline,
       the observer catches. */
    apps().forEach(observeReplyArrows);

    /* Judge.me loads asynchronously, so it may not be present at
       DOMContentLoaded but will be by window load. */
    if (!bindLifecycle()) window.addEventListener('load', bindLifecycle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
